import { omit } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toError } from 'shared/util/toError';
import { Permission } from 'shared/permissions';
import { OrgId, BuildingId, BuildingOrg, BuildingLinkCode } from 'shared/models';
import { getBuildingByUrlSlug, getBuildingDevices, HasUrlSlug } from 'selectors/buildingsSelectors';
import { useAsync, useContracts, useOrgs, useSession } from 'hooks';
import { State as ReduxState } from 'reducers';
import { callApi } from 'apiClient';
import { useParams } from 'util/reactRouterHooks';
import { DebugLink } from 'ui/shared/DebugLink';
import { ErrorMessage } from 'ui/shared/messages';
import { BuildingOrgDialog } from '../buildingOrgDialog/BuildingOrgDialog';
import { BuildingLinkCodesList } from '../buildingLinkCodesList/BuildingLinkCodesList';
import { BuildingOrgList } from '../buildingOrgList/BuildingOrgList';
import { BillingWarnings, getBillingServicesWarnings } from './billingWarnings/BillingWarnings';

require('./BuildingOrgsPage.css');

type BuildingOrgDialogState = {
    visible: boolean;
    orgId: OrgId | null;
    isNew: boolean;
};

type State = {
    busy: boolean;
    loaded: boolean;
    buildingOrgs: Record<OrgId, BuildingOrg>;
    buildingLinkCodes: BuildingLinkCode[];
    errorKey?: string;
};

export const BuildingOrgsPage = () => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();
    const { isMounted } = useAsync();

    const params = useParams<HasUrlSlug>();
    const building = useSelector((state: ReduxState) => getBuildingByUrlSlug(state, params));
    const buildingId = building && building.id;
    const hasEcs = !!(building && building.hasEcs);

    const { contracts, isBusy: isBusyContracts, isLoaded, errorKey: contractsErrorKey } = useContracts({ buildingId });
    const { busy: isBusyOrgs } = useOrgs();
    const [ state, setState ] = useState<State>({
        busy: false,
        loaded: false,
        buildingOrgs: {},
        buildingLinkCodes: [],
    });

    const isBusy = isBusyContracts || isBusyOrgs;
    const isLoading = (!isLoaded && !contractsErrorKey) || (!state.loaded && !state.errorKey);

    const [ buildingOrgDialogState, setBuildingOrgDialogState ] = useState<BuildingOrgDialogState>({
        visible: false,
        orgId: null,
        isNew: false,
    });

    const devices = useSelector((state: ReduxState) => (buildingId && getBuildingDevices(state, { buildingId })) || {});
    
    const { unbilledServices, overbilledServices } = useMemo(() => {
        return getBillingServicesWarnings(state.buildingOrgs || {}, contracts, hasEcs, devices, isLoading);
    }, [ state.buildingOrgs, contracts, hasEcs, devices, isLoading ]);

    const loadBuildingOrgs = useCallback((buildingId: BuildingId) => {
        setState(state => ({
            ...omit(state, 'errorKey'),
            busy: true,
        }));
        void (async () => {
            try {
                const { buildingOrgs, linkCodes } = await callApi('GET', `/api/buildings/${buildingId}/orgs`);
                if (isMounted()) {
                    setState({
                        busy: false,
                        loaded: true,
                        buildingOrgs,
                        buildingLinkCodes: linkCodes,
                    });
                }
            }
            catch (e) {
                const err = toError(e);
                isMounted() && setState(state => ({
                    ...state,
                    busy: false,
                    errorKey: err.message,
                }));
            }
        })();
    }, [ setState, isMounted ]);

    const handleLinkCodeGenerated = useCallback((buildingLinkCode: BuildingLinkCode) => {
        setState(state => ({
            ...omit(state, 'errorKey'),
            buildingLinkCodes: [
                ...state.buildingLinkCodes,
                buildingLinkCode,
            ],
        }));
    }, [ buildingId, setState ]);

    const handleBuildingOrgsChanged = useCallback((buildingOrgs: Record<OrgId, BuildingOrg>) => {
        setState(state => ({
            ...state,
            buildingOrgs,
        }));
    }, [ setState ]);

    const handleRemoveLinkCode = useCallback((linkCode: string) => {
        setState(state => ({
            ...omit(state, 'errorKey'),
            busy: true,
        }));
        void (async () => {
            try {
                await callApi('DELETE', `/api/buildings/${buildingId}/orgs/${linkCode}`);
                setState(state => ({
                    ...omit(state, 'errorKey'),
                    buildingLinkCodes: state.buildingLinkCodes.filter(lc => lc.linkCode !== linkCode),
                }));
            }
            catch (e) {
                const err = toError(e);
                isMounted() && setState(state => ({
                    ...state,
                    busy: false,
                    errorKey: err.message,
                }));
            }
        })();
    }, [ buildingId, setState, isMounted ]);

    const handleCloseBuildingOrg = useCallback(() => {
        setBuildingOrgDialogState(state => ({
            ...state,
            visible: false,
        }));
    }, [ setBuildingOrgDialogState ]);

    const handleClickAddOrg = useCallback(() => {
        setBuildingOrgDialogState({
            orgId: null,
            visible: true,
            isNew: true,
        });
    }, [ setBuildingOrgDialogState ]);

    const handleClickEdit = useCallback((orgId: OrgId) => {
        setBuildingOrgDialogState({
            orgId,
            visible: true,
            isNew: false,
        });
    }, [ setBuildingOrgDialogState ]);

    useEffect(() => {
        if (buildingId) {
            loadBuildingOrgs(buildingId);
        }
    }, [ buildingId, loadBuildingOrgs ]);

    return (
        <div id="building-orgs-page" className="page">
            <div>
                <ErrorMessage message={
                    (state.errorKey && t([ `ERROR.${state.errorKey}`, 'SERVER_ERROR' ]))
                    || (contractsErrorKey && t([ `ERROR.${contractsErrorKey}`, 'SERVER_ERROR' ]))
                }/>
                <BillingWarnings
                    unbilledServices={ unbilledServices }
                    overbilledServices={ overbilledServices }
                />
                <h1 className="ui header">{ t(`ORGS.LINK`) }</h1>
                <BuildingOrgList
                    contracts={ contracts }
                    buildingOrgs={ state.buildingOrgs }
                    loading={ state.busy }
                    unbilledServices={ unbilledServices }
                    overbilledServices={ overbilledServices }
                    canEdit={ hasPermission(buildingId, Permission.ManageBuildingLinks) || hasPermission(null, Permission.ManageBilling) }
                    onEdit={ handleClickEdit }
                />
                { hasPermission(buildingId, Permission.ManageBuildingLinks) && state.buildingLinkCodes.length > 0 &&
                    <>
                        <h2>
                            { t('BUILDING.ORGS.LABEL.PENDING') }
                        </h2>
                        <BuildingLinkCodesList
                            linkCodes={ state.buildingLinkCodes }
                            loading={ state.busy }
                            onClickDelete={ handleRemoveLinkCode }
                        />
                    </>
                }
            </div>
            <div>
                { hasPermission(buildingId, Permission.ManageBuildingLinks) && state.buildingLinkCodes.length < 3 && // Limit users from generating many link codes
                    <button
                        type="button"
                        className="ui basic button"
                        onClick={ handleClickAddOrg }
                    >
                        { t('COMMAND.ADD_ORG') }
                    </button>
                }
                <DebugLink
                    sql={
                        `--[Building-Org Links]\n` +
                        `SELECT *\nFROM building_org\nWHERE building_id = '${buildingId}';\n\n` +
                        `--[Linked Organizations]\n` +
                        `SELECT o.*\nFROM building_org bo,\n    orgs o\nWHERE bo.org_id = o.id\n    AND building_id = '${buildingId}';\n\n` +
                        `--[Pending Links]\n` +
                        `SELECT * FROM building_link_codes WHERE building_id = '${buildingId}' ORDER BY created;\n`
                    }
                />
            </div>
            
            { buildingId &&
                <BuildingOrgDialog
                    { ...buildingOrgDialogState }
                    buildingId={ buildingId }
                    busy={ state.busy || isBusy }
                    contracts={ contracts }
                    buildingOrgs={ state.buildingOrgs }
                    onLinkCodeGenerated={ handleLinkCodeGenerated }
                    onBuildingOrgsChanged={ handleBuildingOrgsChanged }
                    onClose={ handleCloseBuildingOrg }
                    hasEcs={ hasEcs }
                />
            }
        </div>
    );
};
